import Box from "@mui/material/Box";

import type { AgeGroupCode, EventKind, LevelCode, MatrixCell } from "@/libs/tournamentSetupApi";

import { TermHelp } from "./term-help";
import { STAFF_FONT, tone } from "./ui";

export const KINDS: { kind: EventKind; label: string; junior: string }[] = [
  { kind: "MS", label: "Men’s singles", junior: "Boys’" },
  { kind: "WS", label: "Women’s singles", junior: "Girls’" },
  { kind: "MD", label: "Men’s doubles", junior: "Boys’" },
  { kind: "WD", label: "Women’s doubles", junior: "Girls’" },
  { kind: "XD", label: "Mixed doubles", junior: "Junior mixed" },
];
export const LEVEL_COLUMNS: LevelCode[] = ["open", "A", "B", "C"];
export const AGE_COLUMNS: AgeGroupCode[] = ["U13", "U15", "U17", "35+", "45+"];

export type SecondAxis = { ageGroups: AgeGroupCode[]; levels: LevelCode[] } | null;

type Column = { key: string; label: string; level: LevelCode | null; ageGroup: AgeGroupCode | null; separator: boolean };

export function cellKey(cell: MatrixCell): string {
  return `${cell.kind}:${cell.level ?? "-"}:${cell.age_group ?? "-"}`;
}

function columns(secondAxis: SecondAxis): Column[] {
  const levels = LEVEL_COLUMNS.map((level) => ({
    key: `l:${level}`,
    label: level === "open" ? "Open" : level,
    level,
    ageGroup: null,
    separator: false,
  }));
  if (secondAxis) {
    const combined = secondAxis.ageGroups.flatMap((age) =>
      secondAxis.levels.map((level) => ({ key: `c:${age}:${level}`, label: `${age} ${level}`, level, ageGroup: age, separator: false })),
    );
    if (combined[0]) combined[0].separator = true;
    return [...levels, ...combined];
  }
  return [
    ...levels,
    ...AGE_COLUMNS.map((age, index) => ({ key: `a:${age}`, label: age, level: null, ageGroup: age, separator: index === 0 })),
  ];
}

const cellButtonSx = (state: "on" | "has" | "off" | "") => ({
  width: 26,
  height: 26,
  borderRadius: "6px",
  border: `1.5px solid ${state === "on" ? tone.brand : tone.faintLine}`,
  bgcolor: state === "on" ? tone.brand : state === "has" ? tone.chip : tone.surface,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  p: 0,
  cursor: state === "has" || state === "off" ? "default" : "pointer",
  opacity: state === "off" ? 0.35 : 1,
  "&:focus-visible": { outline: `2px solid ${tone.brand}`, outlineOffset: "2px" },
});

/** Disciplines down the side, divisions across; one box per event (F1, F6). */
export function EventsMatrix({
  selected,
  existing = [],
  secondAxis,
  onToggle,
  showCreates,
}: {
  selected: MatrixCell[];
  existing?: MatrixCell[];
  secondAxis: SecondAxis;
  onToggle: (cell: MatrixCell) => void;
  showCreates?: boolean;
}) {
  const cols = columns(secondAxis);
  const picked = new Set(selected.map(cellKey));
  const has = new Set(existing.map(cellKey));
  const levelCount = LEVEL_COLUMNS.length;
  return (
    <Box component="table" sx={{ borderCollapse: "collapse", fontSize: 12.5, fontFamily: STAFF_FONT }}>
      <thead>
        <tr>
          <th />
          <Box component="th" colSpan={levelCount} sx={{ height: 22, fontSize: 11, color: tone.faint, fontWeight: 700, letterSpacing: ".3px", px: "6px" }}>
            {secondAxis ? "LEVELS (OPEN IS THE TOP LEVEL)" : (
              <>
                LEVELS <TermHelp label="What are levels?" term="level" />
              </>
            )}
          </Box>
          <Box
            component="th"
            colSpan={cols.length - levelCount}
            sx={{ height: 22, fontSize: 11, color: tone.faint, fontWeight: 700, letterSpacing: ".3px", px: "6px", borderLeft: `1px solid ${tone.line}` }}
          >
            {secondAxis ? "AGE GROUP × LEVEL" : (
              <>
                AGE GROUPS <TermHelp label="What are age groups?" term="age_group" />
              </>
            )}
          </Box>
          {showCreates ? <th /> : null}
        </tr>
        <tr>
          <Box component="th" sx={{ textAlign: "left", pl: "14px", height: 26, fontSize: 11.5, fontWeight: 600, color: tone.subtle }}>
            Discipline
          </Box>
          {cols.map((col) => (
            <Box
              component="th"
              key={col.key}
              sx={{ width: secondAxis ? 52 : 46, height: 26, fontSize: 11.5, fontWeight: 600, color: tone.subtle, px: "6px", borderLeft: col.separator ? `1px solid ${tone.line}` : 0 }}
            >
              {col.label}
            </Box>
          ))}
          {showCreates ? (
            <Box component="th" sx={{ textAlign: "left", pl: "14px", fontSize: 11.5, fontWeight: 600, color: tone.subtle }}>
              Creates
            </Box>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {KINDS.map((row) => {
          const rowCount = cols.filter((col) =>
            picked.has(cellKey({ kind: row.kind, level: col.level, age_group: col.ageGroup })),
          ).length;
          const rowLabel = secondAxis ? `${row.label} / ${row.junior}` : row.label;
          return (
            <tr key={row.kind}>
              <Box component="td" sx={{ textAlign: "left", fontWeight: 600, color: tone.ink, pl: "14px", width: secondAxis ? 170 : 150, height: 30, borderTop: `1px solid ${tone.divider}` }}>
                {rowLabel}
              </Box>
              {cols.map((col) => {
                const cell: MatrixCell = { kind: row.kind, level: col.level, age_group: col.ageGroup };
                const key = cellKey(cell);
                const state = has.has(key) ? "has" : picked.has(key) ? "on" : "";
                return (
                  <Box component="td" key={col.key} sx={{ textAlign: "center", height: 30, px: "6px", borderTop: `1px solid ${tone.divider}`, borderLeft: col.separator ? `1px solid ${tone.line}` : 0 }}>
                    <Box
                      aria-checked={state === "has" || state === "on"}
                      aria-disabled={state === "has" || undefined}
                      aria-label={`${row.label}, ${col.label}${state === "has" ? ", already in this tournament" : ""}`}
                      component="button"
                      onClick={() => (state === "has" ? undefined : onToggle(cell))}
                      role="checkbox"
                      sx={cellButtonSx(state)}
                      title={state === "has" ? "Already in this tournament" : undefined}
                      type="button"
                    >
                      {state ? (
                        <svg aria-hidden fill="none" height="14" stroke={state === "on" ? tone.surface : tone.subtle} strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" viewBox="0 0 24 24" width="14">
                          <path d="M5 12l5 5L20 7" />
                        </svg>
                      ) : null}
                    </Box>
                  </Box>
                );
              })}
              {showCreates ? (
                <Box component="td" sx={{ textAlign: "left", pl: "14px", color: tone.muted, borderTop: `1px solid ${tone.divider}` }}>
                  {rowCount ? `${rowCount} event${rowCount === 1 ? "" : "s"}` : "—"}
                </Box>
              ) : null}
            </tr>
          );
        })}
      </tbody>
    </Box>
  );
}
